import { getJobTypeMessages } from '.';

import type { LocaleMessages } from 'shared-types';
import type { GetJobsStatusRes } from 'shared-types/responses';
import type { PendingJobEntry } from 'types';

type ResolvedJobOutcome = {
  notice: {
    link?: { href: string; text: string };
    text: string;
    type: 'error' | 'success';
  };
  resolution: {
    error: { code: string; message: string } | null;
    jobId: number;
    state: 'completed' | 'failed';
  };
};

interface Params {
  entry: PendingJobEntry | undefined;
  job: GetJobsStatusRes['jobs'][number];
  messages: LocaleMessages;
}

/**
 * Maps a resolved job status entry to the notice and the resolution event the
 * poller should emit. Returns null for jobs that are still in progress.
 *
 * Copy preference: tailored copy attached at registration (`entry.messages`),
 * otherwise the generic per-type copy from `getJobTypeMessages`.
 */
const getResolvedJobOutcome = ({
  entry,
  job,
  messages
}: Params): ResolvedJobOutcome | null => {
  if (job.state === 'inprogress') return null;

  const copy = entry?.messages ?? getJobTypeMessages(messages, job.type);
  if (job.state === 'completed') {
    return {
      notice: {
        link: job.url
          ? { href: job.url, text: messages.backgroundJobViewResult }
          : undefined,
        text: copy.success,
        type: 'success'
      },
      resolution: { error: null, jobId: job.id, state: 'completed' }
    };
  }

  return {
    notice: {
      link: job.url
        ? { href: job.url, text: messages.backgroundJobViewDetails }
        : undefined,
      text:
        ('failureByErrorCode' in copy &&
          copy.failureByErrorCode?.[job.error?.code]) ||
        copy.failure,
      type: 'error'
    },
    resolution: { error: job.error, jobId: job.id, state: 'failed' }
  };
};

export default getResolvedJobOutcome;
